import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, Plus, Trash2, RefreshCw, FileSpreadsheet, Users, Download } from 'lucide-react';
import { Label } from '@/components/ui/label';

const ContactList = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [importMode, setImportMode] = useState('add'); // add or replace
  const [newListName, setNewListName] = useState('');
  const [newContact, setNewContact] = useState({ name: '', phone: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchLists = async () => {
    try {
      const response = await axios.get(`${API}/contact-lists`, {
        params: { bina: user?.exten },
      });
      const lists = response.data;
      
      // Fetch actual contact counts for each list
      const listsWithCounts = await Promise.all(
        lists.map(async (list) => {
          try {
            const contactsResponse = await axios.get(`${API}/contacts`, {
              params: { list_id: list.id, bina: user?.exten },
            });
            return { ...list, total_contacts: contactsResponse.data.length };
          } catch (error) {
            console.error(`Error fetching count for list ${list.id}:`, error);
            return list;
          }
        })
      );
      
      setLists(listsWithCounts);
      if (listsWithCounts.length > 0 && !selectedList) {
        setSelectedList(listsWithCounts[0].id);
      }
    } catch (error) {
      console.error('Error fetching lists:', error);
    }
  };

  useEffect(() => {
    if (user?.exten) {
      fetchLists();
    }
  }, [user?.exten]);

  useEffect(() => {
    if (selectedList) {
      fetchContacts();
    }
  }, [selectedList, statusFilter, user?.exten]);

  const fetchContacts = async () => {
    try {
      const params = { list_id: selectedList, bina: user?.exten };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await axios.get(`${API}/contacts`, { params });
      setContacts(response.data);
      
      // Update the total_contacts for the selected list in the lists state
      // Only update if we're showing all contacts (not filtered by status)
      if (selectedList && statusFilter === 'all') {
        setLists(prevLists => 
          prevLists.map(list => 
            list.id === selectedList 
              ? { ...list, total_contacts: response.data.length }
              : list
          )
        );
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) {
      toast.error('Digite um nome para a lista');
      return;
    }
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/contact-lists`, { name: newListName }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Lista criada com sucesso');
      setNewListName('');
      await fetchLists();
      setSelectedList(response.data.id);
    } catch (error) {
      toast.error('Erro ao criar lista');
    }
  };

  const handleDeleteList = async (listId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta lista e todos os seus contatos?')) {
      return;
    }
    try {
      await axios.delete(`${API}/contact-lists/${listId}`);
      toast.success('Lista excluída');
      await fetchLists();
      setSelectedList(lists.length > 1 ? lists[0].id : null);
    } catch (error) {
      toast.error('Erro ao excluir lista');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!selectedList) {
      toast.error('Selecione uma lista primeiro');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('list_id', selectedList);
    formData.append('replace', importMode === 'replace');

    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/contacts/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      const result = response.data;
      toast.success(
        `Importação concluída!\nTotal: ${result.total}\nVálidos: ${result.valid}\nDuplicados: ${result.duplicates}\nInválidos: ${result.invalid}`,
        { duration: 5000 }
      );

      setIsImportOpen(false);
      await fetchContacts();
      await fetchLists();
    } catch (error) {
      toast.error('Erro ao importar contatos');
    }
  };

  const handleAddContact = async () => {
    if (!newContact.name || !newContact.phone) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/contacts`, {
        ...newContact,
        list_id: selectedList,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Contato adicionado');
      setNewContact({ name: '', phone: '' });
      setIsAddContactOpen(false);
      await fetchContacts();
      await fetchLists();
    } catch (error) {
      toast.error('Erro ao adicionar contato');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      toast.success('Contato removido');
      await fetchContacts();
      await fetchLists();
    } catch (error) {
      toast.error('Erro ao remover contato');
    }
  };

  const handleReprocessList = async () => {
    if (!selectedList) return;

    try {
      const response = await axios.post(`${API}/contacts/reprocess/${selectedList}`);
      toast.success(response.data.message);
      await fetchContacts();
    } catch (error) {
      toast.error('Erro ao reprocessar lista');
    }
  };

  const filteredContacts = contacts.filter((contact) =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.phone.includes(searchTerm)
  );

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendente', className: 'bg-slate-100 text-slate-700' },
      calling: { label: 'Chamando', className: 'bg-blue-100 text-blue-700' },
      answered: { label: 'Atendido', className: 'bg-green-100 text-green-700' },
      not_answered: { label: 'Não Atendido', className: 'bg-yellow-100 text-yellow-700' },
      busy: { label: 'Ocupado', className: 'bg-orange-100 text-orange-700' },
      voicemail: { label: 'Caixa Postal', className: 'bg-purple-100 text-purple-700' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const currentList = lists.find((l) => l.id === selectedList);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Agenda de Contatos</h1>
        <p className="text-slate-600">Gerencie suas listas e contatos</p>
      </div>

      {/* Lists Management */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileSpreadsheet size={20} className="text-teal-600" />
              Listas de Contatos
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Nome da nova lista"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                className="w-64"
                data-testid="new-list-input"
              />
              <Button onClick={handleCreateList} data-testid="create-list-button">
                <Plus size={16} className="mr-1" />
                Criar Lista
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {lists.map((list) => (
              <div
                key={list.id}
                className={`flex-shrink-0 border-2 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow ${
                  selectedList === list.id ? 'border-teal-600 bg-teal-50' : 'border-slate-200'
                }`}
                onClick={() => setSelectedList(list.id)}
                data-testid={`list-${list.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800">{list.name}</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteList(list.id);
                    }}
                    className="h-6 w-6 p-0 hover:bg-red-100"
                  >
                    <Trash2 size={14} className="text-red-600" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Users size={14} />
                  <span>{list.total_contacts} contatos</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Actions Bar */}
      {selectedList && (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-3">
              <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-600 hover:bg-teal-700" data-testid="import-button">
                    <Upload size={16} className="mr-2" />
                    Importar CSV
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Importar Contatos</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Modo de Importação</Label>
                      <Select value={importMode} onValueChange={setImportMode}>
                        <SelectTrigger data-testid="import-mode-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="add">Adicionar (mantém existentes)</SelectItem>
                          <SelectItem value="replace">Substituir (apaga tudo)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Arquivo CSV</Label>
                      <Input
                        type="file"
                        accept=".csv"
                        onChange={handleFileUpload}
                        data-testid="file-input"
                      />
                      <p className="text-xs text-slate-500 mt-2">
                        O arquivo deve ter pelo menos as colunas: NomeRazao (nome) e CEL1 (celular). Outros campos são opcionais.
                      </p>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={isAddContactOpen} onOpenChange={setIsAddContactOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="add-contact-button">
                    <Plus size={16} className="mr-2" />
                    Adicionar Contato
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Novo Contato</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Nome</Label>
                      <Input
                        value={newContact.name}
                        onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                        placeholder="Nome completo"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label>Telefone</Label>
                      <Input
                        value={newContact.phone}
                        onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                        placeholder="00000000000"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <Button onClick={handleAddContact} className="w-full" data-testid="save-contact-button">
                      Salvar Contato
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Button variant="outline" onClick={handleReprocessList} data-testid="reprocess-button">
                <RefreshCw size={16} className="mr-2" />
                Reprocessar Lista
              </Button>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Status</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="answered">Atendido</SelectItem>
                  <SelectItem value="not_answered">Não Atendido</SelectItem>
                  <SelectItem value="busy">Ocupado</SelectItem>
                  <SelectItem value="voicemail">Caixa Postal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contacts Table */}
      {selectedList && currentList && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                {currentList.name} - {filteredContacts.length} contatos
              </span>
              <Input
                placeholder="Buscar por nome ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-80"
                data-testid="search-input"
              />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContacts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                        Nenhum contato encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredContacts.map((contact) => (
                      <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                        <TableCell className="font-medium">{contact.name}</TableCell>
                        <TableCell className="font-mono">{contact.phone}</TableCell>
                        <TableCell>{getStatusBadge(contact.status)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteContact(contact.id)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`delete-contact-${contact.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {!selectedList && lists.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <FileSpreadsheet size={48} className="mx-auto text-slate-400 mb-4" />
            <p className="text-lg text-slate-600 mb-2">Nenhuma lista criada ainda</p>
            <p className="text-sm text-slate-500">Crie uma lista para começar a adicionar contatos</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContactList;